import React, { useMemo, useState } from 'react';
import { Search, ListChecks } from 'lucide-react';
import { ROLES } from '../data/framework';
import { ITEM_TYPE_LABEL } from '../utils/framework';
import type { FrameworkItem, ItemType } from '../types/framework';
type Row = FrameworkItem & {
  roleId: string;
  roleName: string;
  domainId: string;
  domainName: string;
};
const ALL_ROWS: Row[] = ROLES.flatMap(role => role.domains.flatMap(domain => [...domain.characteristic, ...domain.behaviour].map(item => ({
  ...item,
  roleId: role.id,
  roleName: role.name,
  domainId: domain.id,
  domainName: domain.name
}))));
const PAGE_SIZE = 50;
export function QuestionLibrary() {
  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState('all');
  const [domainId, setDomainId] = useState('all');
  const [type, setType] = useState<ItemType | 'all'>('all');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const role = ROLES.find(r => r.id === roleId);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_ROWS.filter(r => {
      if (roleId !== 'all' && r.roleId !== roleId) return false;
      if (domainId !== 'all' && r.domainId !== domainId) return false;
      if (type !== 'all' && r.type !== type) return false;
      if (q && !r.criterion.toLowerCase().includes(q) && !r.competency.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [search, roleId, domainId, type]);
  const resetPaging = () => setVisible(PAGE_SIZE);
  const hasFilters = search !== '' || roleId !== 'all' || domainId !== 'all' || type !== 'all';
  const clearFilters = () => {
    setSearch('');
    setRoleId('all');
    setDomainId('all');
    setType('all');
    resetPaging();
  };
  const selectClass = 'px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900';
  return <div className="animate-in fade-in duration-500 space-y-6" data-id="element-1913" data-mp="3f92pliu6g1kf">
      <div data-id="element-1914" data-mp="3f7hvfubrykn3">
        <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1915" data-mp="3fb5pqvp1nb2v">
          Question Library
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-1916" data-mp="3k5jeif97ajyk">
          Every Characteristic and Behaviour statement in the competency
          framework — {ALL_ROWS.length.toLocaleString()} questions across{' '}
          {ROLES.length} roles.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-id="element-1917" data-mp="2sgzuaqz4l73">
        <div className="relative" data-id="element-1918" data-mp="6ry4x32hngbq">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1919" data-mp="6s7zz52e4doa" />
          <input type="text" value={search} onChange={e => {
          setSearch(e.target.value);
          resetPaging();
        }} aria-label="Search questions" placeholder="Search by competency or statement..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-1920" data-mp="3p67bdhk41v0p" />
        </div>
        <div className="flex flex-col md:flex-row gap-3" data-id="element-1921" data-mp="vv07trsasfcx">
          <select aria-label="Role" value={roleId} onChange={e => {
          setRoleId(e.target.value);
          setDomainId('all');
          resetPaging();
        }} className={`${selectClass} md:w-64`} data-id="element-1922" data-mp="vsdzhpxl1vtn">
            <option value="all" data-id="element-1923" data-mp="gz0qdqjat2v4">All roles</option>
            {ROLES.map(r => <option key={r.id} value={r.id} data-id="element-1924" data-mp="cwxe4zot1u8s">
                {r.name}
              </option>)}
          </select>
          <select aria-label="Competency domain" value={domainId} disabled={!role} onChange={e => {
          setDomainId(e.target.value);
          resetPaging();
        }} className={`${selectClass} md:flex-1 disabled:bg-slate-50 disabled:text-slate-400`} data-id="element-1925" data-mp="qsrdos3ov798">
            <option value="all" data-id="element-1926" data-mp="6uvnoh67rrbk">
              {role ? 'All domains' : 'Choose a role to filter by domain'}
            </option>
            {role?.domains.map(d => <option key={d.id} value={d.id} data-id="element-1927" data-mp="gsi8fka72d3g">
                {d.name}
              </option>)}
          </select>
          <div className="inline-flex rounded-md border border-slate-300 p-0.5 bg-slate-50 shrink-0" role="group" aria-label="Question type" data-id="element-1928" data-mp="ku3qrl5hzjl1">
            {(['all', 'characteristic', 'behaviour'] as const).map(t => <button key={t} onClick={() => {
            setType(t);
            resetPaging();
          }} aria-pressed={type === t} className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${type === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-1929" data-mp="ziro9wywm3dm">
                {t === 'all' ? 'All' : ITEM_TYPE_LABEL[t]}
              </button>)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-1930" data-mp="2t0fe6ah523wc">
        <span data-id="element-1931" data-mp="2ozdejsjb7ptu">
          {filtered.length.toLocaleString()}{' '}
          {filtered.length === 1 ? 'question' : 'questions'}
        </span>
        {hasFilters && <button onClick={clearFilters} className="font-medium text-slate-700 hover:text-slate-900" data-id="element-1932" data-mp="2k3558vrsmqul">
            Clear filters
          </button>}
      </div>

      {filtered.length === 0 ? <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center bg-white" data-id="element-1933" data-mp="37xy2jbpwmf39">
          <ListChecks className="h-6 w-6 text-slate-400 mx-auto mb-3" data-id="element-1934" data-mp="330g106e7v25a" />
          <h3 className="text-sm font-medium text-slate-900" data-id="element-1935" data-mp="49e85nhwxotc">
            No questions found
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1936" data-mp="3volk6opmgj30">
            Try a different search or clear the filters.
          </p>
        </div> : <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100" data-id="element-1937" data-mp="ifycq772o70d">
          {filtered.slice(0, visible).map(r => <div key={`${r.roleId}-${r.no}`} className="p-4 sm:p-5" data-id="element-1938" data-mp="9no0ylgj3th0">
              <div className="flex flex-wrap items-center gap-2 mb-1.5" data-id="element-1939" data-mp="dogc5k8yo8fx">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${r.type === 'characteristic' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-teal-50 text-teal-700 border-teal-100'}`} data-id="element-1940" data-mp="dmn5i4yclo7x">
                  {ITEM_TYPE_LABEL[r.type]}
                </span>
                <span className="text-xs text-slate-500" data-id="element-1941" data-mp="3mvr4o8klbqwn">
                  {r.roleName} · {r.domainName}
                </span>
                <span className="text-xs text-slate-400 ml-auto" data-id="element-1942" data-mp="1l1i4k2y5t8fu">#{r.no}</span>
              </div>
              <p className="text-sm text-slate-900 leading-snug" data-id="element-1943" data-mp="1p44ws4yqsvdk">
                {r.criterion}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1944" data-mp="39mp8nswo36on">{r.competency}</p>
            </div>)}
          {visible < filtered.length && <div className="p-4 text-center" data-id="element-1945" data-mp="35hzfibte867f">
              <button onClick={() => setVisible(v => v + PAGE_SIZE)} className="text-sm font-medium text-slate-700 border border-slate-200 px-4 py-2 rounded-md hover:bg-slate-50" data-id="element-1946" data-mp="3jbql594075xv">
                Show more ({(filtered.length - visible).toLocaleString()}{' '}
                remaining)
              </button>
            </div>}
        </div>}
    </div>;
}